/**
 * @Module: Firestore Data Converters
 * @Status: Ready
 * @Summary: Timestamp <-> ISO string conversion for Firestore documents
 * @Description: Firestore stores dates as Timestamp objects. Our app uses
 *   ISO-8601 strings everywhere. These converters sit at the boundary so
 *   no Firestore types leak into components or hooks.
 * @NextSteps: Add toFirestore converter if we need custom write transforms
 */

import { Timestamp } from 'firebase/firestore';
import type { StoredRecipe } from '../../types/stored-recipe';
import type { KitchenProfile } from '../../types/kitchen';

/**
 * Convert a Firestore Timestamp (or ISO string, or null) to an ISO string.
 * Tolerates any shape — safe to call on untrusted document data.
 */
export function timestampToISO(val: unknown): string | null {
  if (!val) return null;
  if (val instanceof Timestamp) return val.toDate().toISOString();
  if (typeof val === 'string') return val;
  return null;
}

/**
 * Hydrate a Firestore document snapshot into a typed StoredRecipe.
 * Applies safe defaults for every field so partial docs never crash.
 */
export function recipeFromFirestore(
  id: string,
  data: Record<string, unknown>,
): StoredRecipe {
  return {
    ...(data as Record<string, any>),
    id,
    createdAt: timestampToISO(data.createdAt) || new Date().toISOString(),
    updatedAt: timestampToISO(data.updatedAt) || new Date().toISOString(),
    lastCookedAt: timestampToISO(data.lastCookedAt),
    isFavorite: (data.isFavorite as boolean) ?? false,
    cookCount: (data.cookCount as number) ?? 0,
    userTags: (data.userTags as string[]) ?? [],
    schemaVersion: (data.schemaVersion as number) ?? 2,
  } as StoredRecipe;
}

/**
 * Hydrate a Firestore document snapshot into a typed SavedMealPlan.
 */
export function mealPlanFromFirestore(
  id: string,
  data: Record<string, unknown>,
): Record<string, any> {
  return {
    ...(data as Record<string, any>),
    id,
    createdAt: timestampToISO(data.createdAt) || new Date().toISOString(),
    updatedAt: timestampToISO(data.updatedAt) || new Date().toISOString(),
  };
}

/**
 * Hydrate a Firestore document snapshot into a typed KitchenProfile.
 */
export function kitchenFromFirestore(
  data: Record<string, unknown>,
): KitchenProfile {
  return {
    cookware: (data.cookware as string[]) ?? [],
    appliances: (data.appliances as string[]) ?? [],
    tools: (data.tools as string[]) ?? [],
    pantryStaples: (data.pantryStaples as string[]) ?? [],
    dietary: (data.dietary as string[]) ?? [],
    skillLevel: (data.skillLevel as string) ?? 'intermediate',
    householdSize: (data.householdSize as number) ?? 1,
    notes: (data.notes as string) ?? '',
    rawText: (data.rawText as string) ?? '',
  };
}
